import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ConsultarService } from '../services/consultar.service';
import { RegistroService } from 'src/app/registro.service';
import { PresentacionProductoComercializador } from '../../registro-productos-comercializadores/interfaces/producto-comercializador.interface';
import { PaginacionSuperiorComponent } from '../../consultar-productos-comercializadores/components/control-paginacion/paginacion-superior';
import { TablaProductosComponent } from '../../consultar-productos-comercializadores/components/tabla-productos/tabla-productos.component';
import Swal from 'sweetalert2';
import { FiltrosPresentaciones } from '../../interface/productos-filtros.interface';

/**
 * Componente para consultar y gestionar productos de comercializadores
 *
 * Este componente permite visualizar, paginar y cambiar el estado de los productos
 * de los comercializadores registrados en el sistema.
 *
 * @example
 * <app-consultar-productos-comercializadores></app-consultar-productos-comercializadores>
 *
 * @since 1.0.0
 */
@Component({
    selector: 'app-consultar-productos-comercializadores',
    standalone: true,
    imports: [
        CommonModule,
        FormsModule,
        PaginacionSuperiorComponent,
        TablaProductosComponent,
    ],
    templateUrl: './consultar-productos-comercializadores.component.html',
    styleUrls: ['./consultar-productos-comercializadores.component.css'],
})
export class ConsultarProductosComercializadoresComponent implements OnInit {
    /**
     * Lista de presentaciones de productos de comercializadores
     */
    presentaciones: PresentacionProductoComercializador[] = [];

    /**
     * Datos para la paginación
     */
    paginaActual = 1;
    limite = 10;
    totalPaginas = 0;
    totalRegistros = 0;

    filtros: FiltrosPresentaciones = {
        numeroRegistroIca: '',
        nombreProducto: '',
        tipoProductoId: undefined,
        estado: '',
    };

    /**
     * Constructor del componente
     *
     * @param consultarService - Servicio para realizar consultas de productos
     * @param registroService - Servicio para gestionar el registro y selección de empresas
     */
    constructor(
        private readonly consultarService: ConsultarService,
        private readonly registroService: RegistroService
    ) {}

    /**
     * Carga los registros iniciales de productos de comercializadores
     */
    ngOnInit() {
        this.cargarRegistros();
    }

    /**
     * Carga los registros de productos de comercializadores desde el servicio
     *
     * @returns Promise<void>
     *
     * @example
     * await this.cargarRegistros();
     */
    async cargarRegistros() {
        const filtrosParaServicio = {
            ...this.filtros,
            tipoProductoId: this.filtros.tipoProductoId
                ? Number(this.filtros.tipoProductoId)
                : undefined,
        };

        try {
            const respuesta =
                await this.consultarService.getProductosComercializador(
                    this.paginaActual.toString(),
                    this.limite.toString(),
                    this.registroService.empresaSelected,
                    filtrosParaServicio
                );
            this.presentaciones = respuesta.data || [];
            this.totalRegistros = respuesta.total || 0;
            this.totalPaginas = respuesta.totalPages || 1;
        } catch (err) {
            Swal.fire('', err, 'error');
            console.error('Error al cargar registros:', err);
        }
    }

    /**
     * Navega a la página anterior si existe
     *
     * Decrementa el número de página actual y recarga los registros
     * solo si la página actual es mayor que 1.
     *
     * @example
     * this.paginaAnterior();
     */
    paginaAnterior() {
        if (this.paginaActual > 1) {
            this.paginaActual--;
            this.cargarRegistros();
        }
    }

    /**
     * Navega a la página siguiente si existe
     *
     * Incrementa el número de página actual y recarga los registros
     * solo si la página actual es menor que el total de páginas.
     *
     * @example
     * this.paginaSiguiente();
     */
    paginaSiguiente() {
        if (this.paginaActual < this.totalPaginas) {
            this.paginaActual++;
            this.cargarRegistros();
        }
    }

    /**
     * Cambia el límite de registros por página
     *
     * Actualiza el límite de registros, reinicia la paginación a la primera página
     * y recarga los registros con el nuevo límite.
     *
     * @param nuevoLimite - Nuevo número de registros por página
     *
     * @example
     * this.cambiarLimite(25);
     */
    cambiarLimite(nuevoLimite: number) {
        this.limite = nuevoLimite;
        this.paginaActual = 1;
        this.cargarRegistros();
    }

    /**
     * Cambia el estado de un registro (activo/inactivo)
     *
     * Alterna el estado del registro entre activo e inactivo y actualiza
     * el estado en el servidor mediante el servicio.
     *
     * @param registro - Registro cuyo estado se va a cambiar
     *
     * @example
     * this.cambiarEstado(miRegistro);
     */
    cambiarEstado(registro: PresentacionProductoComercializador) {
        const nuevoEstado =
            registro.registroProducto.estado === 'activo'
                ? 'inactivo'
                : 'activo';
        registro.registroProducto.estado = nuevoEstado;
        this.consultarService.actualizarEstado(
            registro.registroProducto.id,
            nuevoEstado
        );
    }

    /**
     * Maneja los eventos de filtrado desde la tabla
     */
    onFiltroAplicado(evento: { columna: string; valor: string }): void {
        switch (evento.columna) {
            case 'tipoProducto':
                this.filtros.tipoProductoId = evento.valor
                    ? Number(evento.valor)
                    : undefined;
                break;
            case 'numeroRegistroIca':
                this.filtros.numeroRegistroIca = evento.valor;
                break;
            case 'nombreComercial':
                this.filtros.nombreProducto = evento.valor;
                break;
            case 'empaque':
                this.filtros.empaque = evento.valor;
                break;
            case 'estado':
                this.filtros.estado = evento.valor;
                break;
        }
        this.paginaActual = 1;
        this.cargarRegistros();
    }

    /**
     * Limpia todos los filtros
     */
    limpiarFiltros(): void {
        this.filtros = {
            numeroRegistroIca: '',
            nombreProducto: '',
            tipoProductoId: undefined,
            estado: '',
        };
        this.paginaActual = 1;
        this.cargarRegistros();
    }

    /**
     * Verifica si hay filtros activos
     */
    hayFiltrosActivos(): boolean {
        return Object.values(this.filtros).some(
            (valor) => valor !== null && valor !== undefined && valor !== ''
        );
    }
}
